import DocumentTitle from 'react-document-title';
import { useNavigate } from 'react-router-dom';
import Sticky from 'react-stickynode';

import { Box } from '@hivebrite/design-system/Box';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useUIContext } from 'hb-react/frontoffice/contexts/UIContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import EventActions from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/EventActions';
import EventInformations from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/EventInformations';
import { TicketsAdditionalInfo } from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/TicketsAdditionalInfo';
import HighlightedEvents from 'hb-react/shared/apps/Events/components/HighlightedEvents';
import { EventProvider } from 'hb-react/shared/apps/Events/contexts/EventContext';
import { useEvent } from 'hb-react/shared/apps/Events/hooks';
import { type EventsDetailsPageProps } from 'hb-react/shared/apps/Events/startup/types';
import { getEventDedicatedPath } from 'hb-react/shared/apps/Events/utils/getEventDedicatedUrl';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

import EventHeader from './components/EventHeader';
import EventSummary from './components/EventSummary';
import { Grid } from './components/Grid';
import { Skeleton } from './components/Skeleton';

export const DetailsPage = ({ config }: EventsDetailsPageProps) => {
  const navigate = useNavigate();

  const breakpoint = useBreakpoint();
  const { headerHeight } = useUIContext();

  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const timeZoneConfiguration = useTimeZoneConfiguration();
  const topicContext = useTopicContext();

  const { event, isLoading } = useEvent();

  if (isLoading || !event) {
    return <Skeleton />;
  }

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          networkName: getPageTitleFromNetwork(currentNetwork),
          title: event?.title,
        })}
      />
      <EventProvider event={event} config={config}>
        <Box
          m="auto"
          flexDirection="column"
          maxWidth={{
            _: '100%',
            m: '724px',
            l: '960px',
            xl: '1146px',
          }}
          mt={{ _: 0, m: 6 }}
          pb={6}
        >
          <Grid id="event-wrapper">
            <header>
              <EventHeader
                height={{ s: 'auto', m: 201, l: 271, xl: 338 }}
                mb={4}
              />
            </header>
            <div className="additional-info">
              <TicketsAdditionalInfo />
            </div>
            <main>
              <EventInformations flexGrow={1} flexShrink={1} />
            </main>
            <aside>
              <EventSummary />

              <Sticky
                enabled={breakpoint !== 's'}
                top={
                  headerHeight > 0
                    ? headerHeight
                    : document.querySelector('.header-container')?.clientHeight
                }
                bottomBoundary="#event-wrapper"
              >
                <Box mt={4} pb={{ _: 0, m: 4 }} minWidth={0}>
                  <EventActions />
                </Box>
              </Sticky>
            </aside>
          </Grid>

          {event.show_related_events && (
            <HighlightedEvents
              eventId={event.id}
              headline={event.headline_related_events}
              onEventCardClick={(event) => {
                navigate(
                  getEventDedicatedPath({
                    hasUser: currentUser,
                    event,
                    topicId: topicContext.topicId,
                    isWhitelabelGroup: !!topicContext.whitelabelURL,
                  }),
                );
              }}
              timezoneConfiguration={timeZoneConfiguration}
            />
          )}
        </Box>
      </EventProvider>
    </>
  );
};
